/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: TimeConditionFormView.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Form view for creating and editing Time Conditions.
    Manages time-based routing with match/nomatch destinations.

 🔧 Notes:
    - Integrates with Time Groups
    - Supports multiple destination types
    - Domain-scoped data

 🔁 Last Updated: 2025-11-07 by GPT-5 Codex
────────────────────────────────────────────────────────────────*/

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Clock, Search, X, Copy, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { Button } from '../../components/ui/button';
import { fetchTimeCondition, createTimeCondition, updateTimeCondition, fetchTimeConditions } from './api/timeConditionApi';
import { TimeCondition } from './types';
import type { TimeConditionFormData, TimeGroupOption } from './formTypes';
import { fetchTimeGroups } from '../timeGroups/api/timeGroupApi';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { ModuleFormHeader } from '../../components/layouts';
import { useNavigationStore } from '../../store/navigation';
import { deleteTimeConditionById, navigateToTimeConditionDuplicate } from './timeConditionsActions';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { TimeConditionGeneralSection } from './components/TimeConditionGeneralSection';
import { TimeConditionRoutingSection } from './components/TimeConditionRoutingSection';



const DEFAULT_FORM: TimeConditionFormData = {
  name: '',
  description: '',
  groupId: null,
  context: '',
  toggleCode: '',
  authPin: '',
  status: 'default',
  blfInverted: false,
  destinationMatchModule: '',
  destinationMatchValue: '',
  destinationNomatchModule: '',
  destinationNomatchValue: '',
};

const allowedStatuses = new Set(['default', 'forced-match', 'forced-nomatch']);

const generateContextFromName = (rawName: string) =>
  rawName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .substring(0, 80)
    .replace(/^tc_/, '')
    .replace(/^/, 'tc_');

const deepClone = <T,>(value: T): T =>
  typeof structuredClone === 'function'
    ? structuredClone(value)
    : JSON.parse(JSON.stringify(value));

export default function TimeConditionFormView() {
  const { t } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { setCurrentPageTitle } = useNavigationStore();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const resetParam = searchParams.get('_reset');
  const isEditMode = Boolean(id && id !== 'new');
  const navigate = useNavigate();

  const [formData, setFormData] = useState<TimeConditionFormData>(deepClone(DEFAULT_FORM));
  const [originalData, setOriginalData] = useState<TimeConditionFormData>(deepClone(DEFAULT_FORM));

  const [timeGroups, setTimeGroups] = useState<TimeGroupOption[]>([]);
  const [loadingTimeGroups, setLoadingTimeGroups] = useState(false);

  const [isLoading, setIsLoading] = useState(isEditMode || Boolean(duplicateId));
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListConditions, setQuickListConditions] = useState<any[]>([]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const lastSaveTimestamp = useRef(0);
  const hasLoadedTimeGroupsRef = useRef(false);
  const lastLoadedIdRef = useRef<string | null>(null);
  const isLoadingRef = useRef<boolean>(false);
  const currentIdRef = useRef<string | undefined>(undefined);
  const hasLoadedInitialDataRef = useRef<boolean>(false);
  const lastIdRef = useRef<string | undefined>(id);
  const lastResetParamRef = useRef<string | null>(null);

  // Stable refs for functions to prevent re-creations
  const showErrorRef = useRef(showError);
  const showSuccessRef = useRef(showSuccess);
  const showInfoRef = useRef(showInfo);
  const tRef = useRef(t);
  const navigateRef = useRef(navigate);

  useEffect(() => {
    showErrorRef.current = showError;
    showSuccessRef.current = showSuccess;
    showInfoRef.current = showInfo;
    tRef.current = t;
    navigateRef.current = navigate;
  }, [showError, showSuccess, showInfo, t, navigate]);

  // Reset form when navigating to 'new' after Save and New
  useEffect(() => {
    const currentId = id || 'new';
    const previousId = lastIdRef.current;
    const currentResetParam = resetParam;

    // If we're navigating from edit/create mode to 'new', reset the form
    // Also check for _reset query param which is set by useSaveWithAction
    const shouldReset = (!id || id === 'new') &&
      !isEditMode &&
      (previousId !== currentId ||
        (currentResetParam && currentResetParam !== lastResetParamRef.current));

    if (shouldReset) {
      setFormData(deepClone(DEFAULT_FORM));
      setOriginalData(deepClone(DEFAULT_FORM));
      setErrors({});

      // Clear the reset param from URL to prevent repeated resets
      if (currentResetParam) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('_reset');
        const newSearch = newSearchParams.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
      }
    }

    lastIdRef.current = currentId;
    lastResetParamRef.current = currentResetParam;
  }, [id, isEditMode, resetParam, searchParams]);

  const pageTitle = useMemo(
    () => (isEditMode ? `${t('timeConditions.editCondition')} - ${formData.name || ''}` : t('timeConditions.createCondition')),
    [isEditMode, t, formData.name],
  );

  const loadTimeGroups = useCallback(async () => {
    // Only load once on mount
    if (hasLoadedTimeGroupsRef.current) {
      return;
    }
    hasLoadedTimeGroupsRef.current = true;

    setLoadingTimeGroups(true);
    try {
      const result = await fetchTimeGroups(
        { page: 1, limit: 100, sortBy: 'name', sortOrder: 'asc' },
      );
      setTimeGroups(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      showErrorRef.current(tRef.current('timeConditions.messages.loadTimeGroupsError', { defaultValue: tRef.current('timeConditions.messages.loadError') }));
    } finally {
      setLoadingTimeGroups(false);
    }
  }, []);

  useEffect(() => {
    loadTimeGroups();
  }, [loadTimeGroups]);

  const applyLoadedData = useCallback(
    (data: Partial<TimeConditionFormData>, { duplicate }: { duplicate: boolean }) => {
      const populated: TimeConditionFormData = {
        ...DEFAULT_FORM,
        ...data,
      } as TimeConditionFormData;

      if (!populated.context) {
        populated.context = generateContextFromName(populated.name);
      }

      if (duplicate) {
        const copySuffix = tRef.current('common:copySuffix');
        populated.name = `${populated.name} ${copySuffix}`.trim();
        populated.context = generateContextFromName(populated.name);
        // Batch state updates to prevent re-renders during render cycle
        setTimeout(() => {
          setOriginalData(deepClone(DEFAULT_FORM));
          setFormData(deepClone(populated));
        }, 0);
      } else {
        // Batch state updates to prevent re-renders during render cycle
        setTimeout(() => {
          setOriginalData(deepClone(populated));
          setFormData(deepClone(populated));
        }, 0);
      }
    },
    [],
  );

  const applyLoadedDataRef = useRef(applyLoadedData);
  useEffect(() => {
    applyLoadedDataRef.current = applyLoadedData;
  }, [applyLoadedData]);

  const loadTimeConditionFn = useCallback(
    async (conditionId: string, { duplicate }: { duplicate: boolean }) => {
      // Prevent duplicate loads
      if (isLoadingRef.current || lastLoadedIdRef.current === conditionId) {
        return;
      }

      isLoadingRef.current = true;
      lastLoadedIdRef.current = conditionId;
      setIsLoading(true);

      try {
        const condition = await fetchTimeCondition(conditionId);
        applyLoadedDataRef.current(
          {
            name: condition.name || '',
            description: condition.description || '',
            groupId: condition.groupId ?? (condition as any).group_id ?? null,
            context: condition.context || '',
            toggleCode: condition.toggleCode ?? (condition as any).toggle_code ?? '',
            authPin: condition.authPin ?? (condition as any).auth_pin ?? '',
            status: allowedStatuses.has(condition.status)
              ? condition.status
              : 'default',
            blfInverted: Boolean(condition.blfInverted ?? (condition as any).blf_inverted ?? false),
            destinationMatchModule: condition.destinationMatchModule ?? (condition as any).destination_match_module ?? '',
            destinationMatchValue: condition.destinationMatchValue ?? (condition as any).destination_match_value ?? '',
            destinationNomatchModule: condition.destinationNomatchModule ?? (condition as any).destination_nomatch_module ?? '',
            destinationNomatchValue: condition.destinationNomatchValue ?? (condition as any).destination_nomatch_value ?? '',
          },
          { duplicate },
        );
      } catch (error) {
        lastLoadedIdRef.current = null; // Allow retry on error
        showErrorRef.current(tRef.current('timeConditions.messages.loadError'));
        navigateRef.current('/pbx/incoming-tools/time-conditions');
      } finally {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    },
    [],
  );

  const loadTimeConditionRef = useRef(loadTimeConditionFn);
  useEffect(() => {
    loadTimeConditionRef.current = loadTimeConditionFn;
  }, [loadTimeConditionFn]);

  // Load initial data only once
  useEffect(() => {
    if (hasLoadedInitialDataRef.current) return;
    hasLoadedInitialDataRef.current = true;
  }, []);

  // Reset load guard on unmount — ensures navigating back to the same ID always reloads fresh data
  useEffect(() => {
    return () => {
      lastLoadedIdRef.current = null;
      currentIdRef.current = undefined;
    };
  }, []);

  // Main effect for loading data when id/duplicateId changes
  useEffect(() => {
    const loadId = duplicateId || id;
    const idChanged = currentIdRef.current !== loadId;

    if (idChanged) {
      currentIdRef.current = loadId;
    }

    if (isEditMode && id && idChanged) {
      setTimeout(() => {
        loadTimeConditionRef.current(id, { duplicate: false });
      }, 0);
    } else if (duplicateId && idChanged) {
      setTimeout(() => {
        loadTimeConditionRef.current(duplicateId, { duplicate: true });
      }, 0);
    } else if (!isEditMode && !duplicateId) {
      setTimeout(() => {
        lastLoadedIdRef.current = null;
      }, 0);
    }
  }, [duplicateId, id, isEditMode]);

  useEffect(() => {
    if (!isEditMode && !duplicateId && formData.name.trim().length > 0) {
      const generated = generateContextFromName(formData.name);
      setFormData((prev) => ({ ...prev, context: generated }));
    }
  }, [formData.name, isEditMode, duplicateId]);

  const handleFieldChange = <K extends keyof TimeConditionFormData>(field: K, value: TimeConditionFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as string]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as string];
        return next;
      });
    }
  };

  const hasChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, originalData]);

  const validateForm = () => {
    const nextErrors: Record<string, string> = {};

    if (!formData.name || formData.name.trim().length < 3) {
      nextErrors.name = t('timeConditions.validation.nameMinLength');
    }

    if (!formData.groupId || formData.groupId <= 0) {
      nextErrors.groupId = t('timeConditions.validation.timeGroupRequired');
    }

    if (!formData.context || formData.context.trim().length === 0) {
      nextErrors.context = t('timeConditions.validation.contextRequired');
    }

    setErrors(nextErrors);
    return { isValid: Object.keys(nextErrors).length === 0, errors: nextErrors };
  };

  const handleSubmit = async (event?: React.FormEvent): Promise<TimeCondition | undefined> => {
    event?.preventDefault();

    // Double-save prevention check before setIsSaving
    const now = Date.now();
    if (now - lastSaveTimestamp.current < 1000) {
      return undefined;
    }
    lastSaveTimestamp.current = now;

    const validation = validateForm();
    if (!validation.isValid) {
      const errorMsgs = Object.values(validation.errors).filter(msg => typeof msg === 'string').join(' • ');
      showErrorRef.current(`${tRef.current('timeConditions.messages.fixValidationError', { defaultValue: tRef.current('common.fixErrors') })}${errorMsgs ? ': ' + errorMsgs : ''}`);
      return undefined;
    }
    setIsSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        groupId: formData.groupId,
        context: formData.context.trim() || generateContextFromName(formData.name),
        toggleCode: formData.toggleCode.trim() || null,
        authPin: formData.authPin.trim() || null,
        status: allowedStatuses.has(formData.status) ? formData.status : 'default',
        blfInverted: formData.blfInverted,
        destinationMatchModule: formData.destinationMatchModule.trim() || null,
        destinationMatchValue: formData.destinationMatchValue.trim() || null,
        destinationNomatchModule: formData.destinationNomatchModule.trim() || null,
        destinationNomatchValue: formData.destinationNomatchValue.trim() || null,
      };

      const result = isEditMode
        ? await updateTimeCondition(id!, payload)
        : await createTimeCondition(payload);

      showSuccessRef.current(
        isEditMode
          ? tRef.current('timeConditions.messages.updateSuccess')
          : tRef.current('timeConditions.messages.createSuccess'),
      );

      setOriginalData(deepClone(formData));

      // Return saved data for useSaveWithAction
      return result;
    } catch (error) {
      showErrorRef.current(error.message || tRef.current('timeConditions.messages.createError'));
      return undefined;
    } finally {
      setIsSaving(false);
    }
  };

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      // Return current data with ID/UUID for navigation purposes
      return { id: Number(id), uuid: String(id) } as TimeCondition;
    }

    return savedData;
  }, [handleSubmit, isEditMode, id]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/incoming-tools/time-conditions',
    newPath: '/pbx/incoming-tools/time-conditions/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => {
      // Time Conditions use ID for navigation
      return `/pbx/incoming-tools/time-conditions/${savedData.id || savedData.uuid}`;
    },
    getFormData: () => ({
      formData
    }),
    storageKey: 'timeConditions:saveAndNewData'
  });

  const handleCancel = () => {
    if (isEditMode) {
      setFormData(deepClone(originalData));
    } else {
      setFormData(deepClone(DEFAULT_FORM));
    }
    setErrors({});
  };

  const handleDeleteConfirm = async () => {
    if (!isEditMode || !id) return;

    setIsDeleting(true);
    try {
      await deleteTimeConditionById(id);
      showSuccessRef.current(tRef.current('timeConditions.messages.deleteSuccess'));
      navigateRef.current('/pbx/incoming-tools/time-conditions');
    } catch (error) {
      showErrorRef.current(error.message || tRef.current('timeConditions.messages.deleteError'));
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleDuplicateFromForm = () => {
    if (!isEditMode || !id) return;
    navigateToTimeConditionDuplicate(navigateRef.current, id);
  };

  const loadQuickList = useCallback(async () => {
    try {
      const result = await fetchTimeConditions(
        { page: 1, limit: 100, sortBy: 'name', sortOrder: 'asc' },
      );
      setQuickListConditions(Array.isArray(result.data) ? result.data : []);
    } catch {
      setQuickListConditions([]);
    }
  }, []);

  useEffect(() => {
    if (showQuickList) {
      loadQuickList();
    }
  }, [loadQuickList, showQuickList]);

  const filteredQuickList = useMemo(() => {
    if (!quickListSearch) return quickListConditions;
    const term = quickListSearch.toLowerCase();
    return quickListConditions.filter(
      (condition) =>
        condition.name?.toLowerCase().includes(term) ||
        condition.description?.toLowerCase().includes(term),
    );
  }, [quickListConditions, quickListSearch]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleQuickListSelect = (condition: any) => {
    setFormData((prev) => ({
      ...prev,
      groupId:
        condition.groupId ??
        condition.group_id ??
        condition.timeGroup?.id ??
        prev.groupId,
      toggleCode: condition.toggleCode ?? condition.toggle_code ?? prev.toggleCode,
      authPin: condition.authPin ?? condition.auth_pin ?? prev.authPin,
      status: allowedStatuses.has(condition.status)
        ? condition.status
        : prev.status,
      blfInverted: Boolean(condition.blfInverted ?? condition.blf_inverted ?? prev.blfInverted),
      destinationMatchModule: condition.destinationMatchModule ?? condition.destination_match_module ?? prev.destinationMatchModule,
      destinationMatchValue: condition.destinationMatchValue ?? condition.destination_match_value ?? prev.destinationMatchValue,
      destinationNomatchModule: condition.destinationNomatchModule ?? condition.destination_nomatch_module ?? prev.destinationNomatchModule,
      destinationNomatchValue: condition.destinationNomatchValue ?? condition.destination_nomatch_value ?? prev.destinationNomatchValue,
    }));
    setShowQuickList(false);
    setQuickListSearch('');
  };

  const statusOptions = useMemo(
    () => [
      { value: 'default', label: t('timeConditions.fields.status.options.default') },
      { value: 'forced-match', label: t('timeConditions.fields.status.options.forcedMatch') },
      { value: 'forced-nomatch', label: t('timeConditions.fields.status.options.forcedNomatch') },
    ],
    [t],
  );

  if (isLoading) {
    return (
      <div className="flex min-h-[16rem] items-center justify-center">
        <span className="text-muted-foreground">{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24">
      <ModuleFormHeader
        icon={<Clock className="h-5 w-5 text-primary" />}
        title={pageTitle}
        backButtonText={t('timeConditions.actions.list')}
        onBack={() => navigateRef.current('/pbx/incoming-tools/time-conditions')}
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('timeConditions.actions.quickList'),
            onClick: () => setShowQuickList(true),
          },
          ...(isEditMode
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('common.duplicate'),
              onClick: handleDuplicateFromForm,
            }]
            : []),
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('timeConditions.actions.add'),
            onClick: () => navigateRef.current('/pbx/incoming-tools/time-conditions/new'),
          },
        ]}
      />

      <form onSubmit={handleSubmit} className="mt-8 space-y-6">
        <TimeConditionGeneralSection
          formData={formData}
          errors={errors}
          timeGroups={timeGroups}
          loadingTimeGroups={loadingTimeGroups}
          onFieldChange={handleFieldChange}
          t={t}
        />

        <TimeConditionRoutingSection
          formData={formData}
          onFieldChange={handleFieldChange}
          t={t}
        />

        <FixedActionBar
          onSaveWithAction={handleSaveWithAction}
          onCancel={handleCancel}
          onDelete={isEditMode ? () => setShowDeleteModal(true) : undefined}
          isEditMode={isEditMode}
          isSaving={isSaving}
          isLoading={isLoading}
          saveText={t('timeConditions.actions.save')}
          cancelText={t('timeConditions.actions.cancel')}
          deleteText={t('common.delete')}
          useSaveDropdown={true}
          saveDropdownStorageKey="timeConditions:saveActionPreference"
        />
      </form>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('timeConditions.messages.confirmDeleteTitle')}
        message={t('timeConditions.messages.confirmDeleteMessage', {
          name: formData.name || 'Time Condition',
        })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('timeConditions.actions.quickListTitle')}
        items={filteredQuickList}
        isLoadingInitial={false}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(condition) => handleQuickListSelect(condition)}
        renderItem={(condition) => (
          <button
            type="button"
            onClick={() => handleQuickListSelect(condition)}
            className="w-full rounded-md border border-border p-3 text-left transition-colors hover:bg-muted/50"
          >
            <div className="font-medium text-foreground">{condition.name}</div>
            {condition.description && (
              <div className="mt-1 text-sm text-muted-foreground">
                {condition.description}
              </div>
            )}
            <div className="mt-1 text-xs text-muted-foreground">
              {condition.timeGroup?.name || condition.timeGroupName || t('timeConditions.fields.timeGroup.label')}
            </div>
          </button>
        )}
        searchPlaceholder={t('timeConditions.actions.quickListSearch')}
        emptyMessage={t('timeConditions.actions.quickListNoResults')}
        headerIcon={<Clock className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}

